import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import CustomDatePicker from "@/components/DateTimePicker";
import api from "@/lib/api";
import { fmtNum, fmtRiyal } from "@/utils/toEnglishDigits";
import {
  ChartLineUp,
  Car as CarIcon,
  MicrosoftExcelLogo,
  User as UserIcon,
  Coin,
  IdentificationBadge,
} from "@phosphor-icons/react";

function firstOfMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function CarRevenue() {
  const [dfrom, setDfrom] = useState(firstOfMonthISO());
  const [dto, setDto] = useState(todayISO());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/car-revenue", {
        params: { dfrom, dto },
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch {
      toast.error("تعذر تحميل أرباح السيارات");
    } finally {
      setLoading(false);
    }
  }, [dfrom, dto]);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    return items.reduce(
      (a, r) => {
        a.trips += r.trips_count || 0;
        a.revenue += r.total_amount || 0;
        return a;
      },
      { trips: 0, revenue: 0 },
    );
  }, [items]);

  const exportExcel = async () => {
    if (!items.length) return toast.error("لا توجد بيانات للتصدير");
    const XLSX = await import("xlsx-js-style");
    const wsData = [
      ["اللوحة", "النوع", "الرحلات", "الإيرادات (ر.س)", "السائقون"],
      ...items.map((r) => [
        r.license_plate,
        r.car_type,
        r.trips_count,
        r.total_amount,
        (r.drivers || []).join(" · "),
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [{ wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 16 }, { wch: 40 }];
    // Header style
    ["A1", "B1", "C1", "D1", "E1"].forEach((c) => {
      ws[c].s = { fill: { fgColor: { rgb: "0F2148" } }, font: { color: { rgb: "C9A961" }, bold: true }, alignment: { horizontal: "center" } };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "أرباح السيارات");
    ws["!dir"] = "rtl";
    if (!wb.Workbook) wb.Workbook = {};
    if (!wb.Workbook.Views) wb.Workbook.Views = [{}];
    wb.Workbook.Views[0].RTL = true;
    XLSX.writeFile(wb, `car-revenue-${dfrom}-to-${dto}.xlsx`);
    toast.success("تم التصدير");
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 py-6" data-testid="car-revenue-page">
        <div className="flex items-end justify-between mb-5 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
              <ChartLineUp size={26} weight="duotone" className="text-primary" />
              أرباح السيارات
            </h1>
            <p className="text-white/50 text-sm mt-1">
              <span className="num">{fmtNum(items.length)}</span> سيارة · مرتّبة تنازلياً حسب الإيرادات
            </p>
          </div>
          <button
            type="button"
            onClick={exportExcel}
            data-testid="btn-car-revenue-excel"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1D6F42]/15 hover:bg-[#1D6F42]/25 border border-[#1D6F42]/60 text-[#5CD8A0] text-sm font-semibold"
          >
            <MicrosoftExcelLogo size={16} weight="fill" /> تصدير Excel
          </button>
        </div>

        {/* Date range */}
        <div className="mb-5 rounded-2xl border border-primary/20 bg-card/50 p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-primary font-bold text-sm">📅 نطاق التقرير:</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-white/60 mb-1 text-right">من</div>
              <CustomDatePicker value={dfrom} onChange={setDfrom} placeholder="YYYY-MM-DD" testId="cr-from" />
            </div>
            <div>
              <div className="text-xs text-white/60 mb-1 text-right">إلى</div>
              <CustomDatePicker value={dto} onChange={setDto} placeholder="YYYY-MM-DD" testId="cr-to" />
            </div>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          <StatCard label="عدد السيارات" value={fmtNum(items.length)} icon={CarIcon} />
          <StatCard label="عدد الرحلات" value={fmtNum(totals.trips)} icon={IdentificationBadge} />
          <StatCard label="الإيرادات" value={fmtRiyal(totals.revenue)} icon={Coin} tone="gold" />
        </div>

        {loading ? (
          <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-white/50 bg-card border border-primary/15 rounded-2xl">
            لا توجد بيانات في هذا النطاق
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((r, i) => (
              <div
                key={r.license_plate + i}
                data-testid={`cr-row-${i}`}
                className="fadeup rounded-2xl border border-primary/20 bg-card p-5 hover:border-primary/50 transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-bold num">{fmtNum(i + 1)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <CarIcon size={16} weight="duotone" className="text-primary" />
                      <span className="text-white font-bold">{r.car_type || "—"}</span>
                      <span className="text-white/50 text-xs num" dir="ltr">
                        {r.license_plate}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-4 text-sm">
                      <span className="text-white/70">
                        الرحلات:{" "}
                        <span className="text-primary font-bold num">{fmtNum(r.trips_count)}</span>
                      </span>
                      <span className="text-white/70">
                        الإيرادات:{" "}
                        <span className="text-primary font-bold num">{fmtRiyal(r.total_amount)}</span>
                      </span>
                    </div>
                    {r.drivers && r.drivers.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-primary/10 flex items-start gap-2">
                        <UserIcon size={13} className="text-primary/60 mt-1 shrink-0" />
                        <div className="flex flex-wrap gap-1.5">
                          {r.drivers.map((d) => (
                            <span
                              key={d}
                              className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 border border-primary/30 text-white/80"
                            >
                              {d}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  return (
    <div className="rounded-2xl border border-primary/20 bg-[#0F2148] p-4 flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tone === "gold" ? "bg-primary/20 border border-primary/40" : "bg-primary/10 border border-primary/25"}`}>
        <Icon size={22} weight="duotone" className="text-primary" />
      </div>
      <div>
        <div className="text-white/60 text-xs">{label}</div>
        <div className="text-white text-xl font-bold num">{value}</div>
      </div>
    </div>
  );
}
